import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { StatusCodes } from 'http-status-codes';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import {
  unLinkedStatement,
  cancelAutoTieoutStatementProcessing,
} from 'api/statement-api';
import DataGrid from 'components/common/data-grid/data-grid-component';
import ConditionalRender from 'components/util/conditional-render-component';
import { setSideBySideViewParams } from 'utils/statement-content-page-utils';
import { DataGridDataApi } from 'models/utils/common/data-grid/data-grid-data-model';
import { getLinkedStatementColumns } from 'constants/util/side-by-side-utils';
import ConfirmationModal from 'components/common/confirmation-modal';
import UnlinkStatementConfirmationModal from './unlink-statement-confirmation-modal';
import { setSourceStatementParams } from 'store/actions/source-statement-actions';
import { useDispatch, useSelector } from 'react-redux';
import { getIsLinkedStatementCheck } from 'store/actions/statement-content-actions';
import { AUTO_TIEOUT_UNLINK_SUCCESS, AUTO_TIEOUT_COMPARE_MODE_CANCELLED } from 'constants/feature/statement-content-constants';
import {
  removeLinkedStatementOnUnlink,
  updateLinkedStmtListStatus,
} from 'store/actions/linked-statement-list-actions';

const SELECT_LINKED_STATEMENT_TAB_BLOCK = 'left-side-select-linked-statement';
const SELECT_LINKED_STATEMENT_TAB_BLOCK_ID =
  'left-side-select-linked-statement-id';

const SelectLinkedStatementTab = ({
  onConfirmStatement,
  isDisableUnlink,
  isCurrentStatementWrappedUp,
  currentUserId,
}) => {
  const urlParams = useParams();
  const dispatch = useDispatch();
  const linkedStatements = useSelector(
    (state) => state?.ui?.sideBySideView?.autoTieout?.linkedStatements,
  );
  const projectUsersList = useSelector(
    (state) => state?.data?.projectUsersList,
  );
  const [selectedLinkedStatement, setSelectedLinkedStatement] = useState({});
  const [
    showUnlinkStatementConfirmationModal,
    setShowUnlinkStatementConfirmationModal,
  ] = useState(false);
  const [showCancelProcessingModal, setShowCancelProcessingModal] =
    useState(false);
  const currentUser = useSelector(state => state?.data?.currentUser);
  const selectedProject = useSelector(state => state?.data?.selectedProject);

  const setSelectedLinkedStatementsParams = (rowData) => {
    const selectedTargetProjectId = urlParams.projectId;
    const targetProjectGeo = selectedTargetProjectId.split('-')[1];
    const params = setSideBySideViewParams(
      `${rowData.clientId}-${targetProjectGeo}`,
      Number(rowData.statementId),
      Number(rowData.revisionId),
      Number(rowData.documentProcessType),
    );
    dispatch(setSourceStatementParams(params));
    onConfirmStatement();
    // this action is to fetch workflow status and workpaper reference details for linked statement in side by side view
    dispatch(getIsLinkedStatementCheck());
  };

  const onSelectLinkedStatement = (rowData) => {
    if (rowData.isStatementDeleted || !isUserHasAccessToSourceStatement(rowData)) return; //If statement is deleted or user does not have access to source statement then do not allow to select
    setSelectedLinkedStatementsParams(rowData);
  };

  const unLinkedStatementRequest = async () => {
    try {
      const response = await unLinkedStatement({
        sourceClientId: selectedLinkedStatement.clientId,
        sourceStatementId: selectedLinkedStatement.statementId,
        sourceRevisionId: selectedLinkedStatement.revisionId,
        targetClientId: parseInt(urlParams.projectId),
        targetStatementId: parseInt(urlParams.statementId),
        targetRevisionId: parseInt(urlParams.revisionId),
      });
      if (response?.data?.result === AUTO_TIEOUT_UNLINK_SUCCESS) {
        dispatch(
          removeLinkedStatementOnUnlink({
            clientId: selectedLinkedStatement.clientId,
            statementId: selectedLinkedStatement.statementId,
            revisionId: selectedLinkedStatement.revisionId,
          }),
        );
      }
      setShowUnlinkStatementConfirmationModal(false);
    } catch (e) {
      console.error('Error unlinking statement:', e);
      setShowUnlinkStatementConfirmationModal(false);
    }
  };

  const handleConfirmUnlink = () => {
    unLinkedStatementRequest();
  };

  const onUnlinkIconClick = (e, rowData) => {
    setSelectedLinkedStatement(rowData);
    setShowUnlinkStatementConfirmationModal(true);
    e.stopPropagation();
  };

  const cancelAutoTieoutStatementProcessingRequest = async () => {
    try {
      const response = await cancelAutoTieoutStatementProcessing({
        sourceClientId: selectedLinkedStatement.clientId,
        sourceStatementId: selectedLinkedStatement.statementId,
        sourceRevisionId: selectedLinkedStatement.revisionId,
        targetClientId: parseInt(urlParams.projectId),
        targetStatementId: parseInt(urlParams.statementId),
        targetRevisionId: parseInt(urlParams.revisionId),
      });
      if (response?.status === StatusCodes.OK) {
        dispatch(
          updateLinkedStmtListStatus({
            clientId: selectedLinkedStatement.clientId,
            statementId: selectedLinkedStatement.statementId,
            revisionId: selectedLinkedStatement.revisionId,
            workflowStatus: AUTO_TIEOUT_COMPARE_MODE_CANCELLED,
          }),
        );
      }
    } catch (e) {
      console.error('Error cancelling auto tieout processing:', e);
      setShowCancelProcessingModal(false);
    }
  };

  const onCancelStatementProcessing = () => {
    cancelAutoTieoutStatementProcessingRequest();
    setShowCancelProcessingModal(false);
  };

  const onCancelIconClick = (e, rowData) => {
    e.stopPropagation();
    setSelectedLinkedStatement(rowData);
    setShowCancelProcessingModal(true);
  };

  const isUserHasAccessToSourceStatement = (rowData) => {
    const projectToGeoMap = currentUser.getProjectToGeoMap();
    const selectedProjecGeo = selectedProject.id.split('-')[1];
    return projectToGeoMap.has(`${rowData.clientId}-${selectedProjecGeo}`);
  };

  return (
    <ConditionalRender dependencies={[linkedStatements]}>
      {linkedStatements.hasLength ? (
        <>
          <DataGrid
            columns={getLinkedStatementColumns(
              onUnlinkIconClick,
              onCancelIconClick,
              isDisableUnlink,
              isCurrentStatementWrappedUp,
              SELECT_LINKED_STATEMENT_TAB_BLOCK,
              currentUserId,
              projectUsersList,
            )}
            className={`${SELECT_LINKED_STATEMENT_TAB_BLOCK}__table-wrapper`}
            tableId={`${SELECT_LINKED_STATEMENT_TAB_BLOCK_ID}`}
            scrollable={true}
            primaryKeyColumn={'revisionId'}
            dataModel={
              new DataGridDataApi({
                apiModel: linkedStatements,
                rowItems: linkedStatements.statementList,
              })
            }
            numberOfVisibleRows={3.5}
            onRowClick={onSelectLinkedStatement}
            shouldDisableRow={(rowData) =>
              rowData.isStatementDeleted ||
              rowData.isStatementStatusNotFound ||
              rowData.isWorkflowStatusCancelled || 
              !isUserHasAccessToSourceStatement(rowData)
            }
            rowClassnames={`${SELECT_LINKED_STATEMENT_TAB_BLOCK}__row`}
            rowTooltipConfig={(rowData) => {
              if (
                rowData.isStatementDeleted ||
                rowData.isStatementStatusNotFound
              ) {
                return {
                  id: `${SELECT_LINKED_STATEMENT_TAB_BLOCK}__deleted-statement-tooltip`,
                  text: 'auto-tieout.linked-statements.table.row.disabled.tooltip.message',
                };
              } else if (rowData.isWorkflowStatusCancelled) {
                return {
                  id: `${SELECT_LINKED_STATEMENT_TAB_BLOCK}__cancelled-statement-tooltip`,
                  text: 'auto-tieout.linked-statements.table.row.cancelled.tooltip.message',
                };
              } else if(!isUserHasAccessToSourceStatement(rowData)) {
                return {
                  id: `${SELECT_LINKED_STATEMENT_TAB_BLOCK}__no-access-statement-tooltip`,
                  text: 'auto-tieout.linked-statements.table.row.no.access.tooltip.message',
                };
              } else {
                return null;
              }
            }}
          />
        </>
      ) : (
        <div
          className={`${SELECT_LINKED_STATEMENT_TAB_BLOCK}__no-linked-statement-msg`}
        >
          <FormattedMessage id="side-by-side-view.compare.left-panel.auto-tieout.left-tab.no-statement.message" />
        </div>
      )}
      {showUnlinkStatementConfirmationModal && (
        <UnlinkStatementConfirmationModal
          setShowUnlinkStatementConfirmationModal={
            setShowUnlinkStatementConfirmationModal
          }
          onConfirm={handleConfirmUnlink}
          statementName={selectedLinkedStatement?.statementName}
        />
      )}
      {showCancelProcessingModal && (
        <ConfirmationModal
          title={
            'auto-tieout.linked-statements.table.column.cancel.modal.header.title'
          }
          textId={
            'auto-tieout.linked-statements.table.column.cancel.modal.body.message'
          }
          onClose={() => setShowCancelProcessingModal(false)}
          onConfirm={onCancelStatementProcessing}
          primaryButtonText={'common.yes'}
          secondaryButtonText={'common.no'}
        />
      )}
    </ConditionalRender>
  );
};

export default SelectLinkedStatementTab;

SelectLinkedStatementTab.propTypes = {
  /** function to confirm statement selection */
  onConfirmStatement: PropTypes.func.isRequired,
  /** boolean to indicate if unlink functionality should be disabled */
  isDisableUnlink: PropTypes.bool,
  /** boolean to indicate if the current statement is wrapped up */
  isCurrentStatementWrappedUp: PropTypes.bool,
};
